import { FormEvent, useMemo, useState } from 'react'

import * as api from 'api/api'
import { AccessRole, Me, Project, ProjectRole, User } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { AdminTabs } from 'components/AdminTabs'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import { Button, Card, EmptyState, ErrorState, Modal, Sheet, Spinner, TextField } from 'components/ui'
import { UserFormModal } from 'components/UserFormModal'
import { ROLE_LABELS, formatDateTime } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const ROLE_OPTIONS: AccessRole[] = ['ADMIN', 'PROJECT_MANAGER', 'SITE_ENGINEER', 'SUPERVISOR']
const INVITE_ROLES: ProjectRole[] = ['SITE_ENGINEER', 'PROJECT_MANAGER', 'SUPERVISOR']

const rolesOf = (user: Me): AccessRole[] => {
  if (user.isAdmin) return ['ADMIN']
  const roles = Array.from(new Set(user.memberships.map((membership) => membership.role)))
  return roles.length ? roles : []
}

const InviteSheet = ({
  projects,
  busy,
  onClose,
  onSent,
}: {
  projects: Project[]
  busy: boolean
  onClose: () => void
  onSent: (email: string, projectId: string, role: ProjectRole) => void
}) => {
  const [email, setEmail] = useState('')
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '')
  const [role, setRole] = useState<ProjectRole>('SITE_ENGINEER')

  const valid = email.trim() && projectId

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!valid) return
    onSent(email.trim(), projectId, role)
  }

  return (
    <Sheet
      title={t('دعوة مستخدمين', 'Invite users')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('إلغاء', 'Cancel')}</Button>
          <Button type="submit" form="invite-form" variant="primary" busy={busy} disabled={!valid}>
            {t('إرسال الدعوة', 'Send invitation')}
          </Button>
        </>
      }
    >
      <form id="invite-form" className="form-grid" onSubmit={submit}>
        <TextField
          label={t('البريد الإلكتروني', 'Email')}
          className="span-2"
          type="email"
          dir="ltr"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@example.com"
          autoFocus
          required
        />
        <div>
          <label className="field__label" htmlFor="invite-project">
            {t('المشروع', 'Project')} <span className="field__required">*</span>
          </label>
          <select id="invite-project" className="input" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="field__label" htmlFor="invite-role">
            {t('الدور', 'Role')} <span className="field__required">*</span>
          </label>
          <select id="invite-role" className="input" value={role} onChange={(event) => setRole(event.target.value as ProjectRole)}>
            {INVITE_ROLES.map((key) => (
              <option key={key} value={key}>
                {ROLE_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
        <p className="field__hint span-2">
          {t(
            'ستظهر رابط الدعوة في سجلات الخادم (لا توجد خدمة بريد مُفعّلة بعد) ويمكن مشاركته يدوياً.',
            'The invitation link appears in the server logs (email is not set up yet) and can be shared manually.'
          )}
        </p>
      </form>
    </Sheet>
  )
}

/** Global user directory, admin-only (Figma: Admin / 20 · Users). Role/Sites columns are derived from real
 *  ProjectMember data; "Client" role and "last active" (presence, not login) from the mockup aren't modelled —
 *  "last active" here is the last successful login instead, which is real and tracked. */
export const UsersPage = () => {
  const { user: me } = useAuth()
  const { data, error, loading, reload, setData } = useAsync(api.fetchUsers, [])
  const { data: projects } = useAsync(api.fetchProjects, [])
  const { data: invitations } = useAsync(api.fetchAllInvitations, [])
  const [creating, setCreating] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [resetting, setResetting] = useState<User | null>(null)
  const [password, setPassword] = useState('')
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<Set<AccessRole>>(new Set())
  const [statusFilter, setStatusFilter] = useState<Set<'ACTIVE' | 'INACTIVE'>>(new Set())
  const [siteFilter, setSiteFilter] = useState<Set<string>>(new Set())
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const siteOptions = useMemo(
    () => Array.from(new Set((data ?? []).flatMap((user) => user.memberships.map((membership) => membership.projectName)))).sort(),
    [data]
  )

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (data ?? []).filter((user) => {
      const roles = rolesOf(user)
      if (roleFilter.size && !roles.some((role) => roleFilter.has(role))) return false
      if (statusFilter.size && !statusFilter.has(user.isActive ? 'ACTIVE' : 'INACTIVE')) return false
      if (siteFilter.size && !user.memberships.some((membership) => siteFilter.has(membership.projectName))) return false
      if (term && !user.name.toLowerCase().includes(term) && !user.email.toLowerCase().includes(term)) return false
      return true
    })
  }, [data, search, roleFilter, statusFilter, siteFilter])

  if (!me?.isAdmin) return <ForbiddenPage message={t('إدارة المستخدمين متاحة لمدير النظام فقط.', 'User management is available to Admin only.')} />

  const replace = (user: User) =>
    setData((current) => current?.map((entry) => (entry.id === user.id ? { ...entry, ...user } : entry)))

  const toggle = <T,>(set: Set<T>, setter: (next: Set<T>) => void, value: T) => {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    setter(next)
  }

  const sendInvite = async (email: string, projectId: string, role: ProjectRole) => {
    const created = await run('invite', () => api.createInvitation(projectId, email, role), t('تم إرسال الدعوة', 'Invitation sent'))
    if (created) setInviting(false)
  }

  const listSeparator = t('، ', ', ')

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('المستخدمون والأدوار', 'Users and roles')}</h1>
          <p className="muted">
            {data
              ? t(
                  `${data.length} شخصاً · ${invitations?.length ?? 0} دعوة معلقة · ${ROLE_OPTIONS.length} أدوار`,
                  `${data.length} people · ${invitations?.length ?? 0} pending invitations · ${ROLE_OPTIONS.length} roles`
                )
              : ''}
          </p>
        </div>
        <div className="toolbar">
          <Button variant="ghost" icon="plus" onClick={() => setCreating(true)}>
            {t('إنشاء مستخدم مباشرة', 'Create user directly')}
          </Button>
          <Button variant="primary" icon="plus" onClick={() => setInviting(true)}>
            {t('دعوة مستخدمين', 'Invite users')}
          </Button>
        </div>
      </div>

      <AdminTabs pendingInvitations={invitations?.length} />

      <div className="toolbar">
        <div className="search">
          <Icon name="search" size={16} />
          <label htmlFor="users-search" className="sr-only">
            {t('بحث', 'Search')}
          </label>
          <input
            id="users-search"
            type="search"
            placeholder={t('بحث بالاسم أو البريد...', 'Search by name or email...')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <details className="dropdown">
          <summary className="btn">
            {t('الدور', 'Role')}
            {roleFilter.size ? ` (${roleFilter.size})` : ''}
          </summary>
          <div className="dropdown__panel">
            {ROLE_OPTIONS.map((role) => (
              <label key={role} className="dropdown__item">
                <input type="checkbox" checked={roleFilter.has(role)} onChange={() => toggle(roleFilter, setRoleFilter, role)} />
                {ROLE_LABELS[role]}
              </label>
            ))}
          </div>
        </details>
        <details className="dropdown">
          <summary className="btn">
            {t('الحالة', 'Status')}
            {statusFilter.size ? ` (${statusFilter.size})` : ''}
          </summary>
          <div className="dropdown__panel">
            <label className="dropdown__item">
              <input type="checkbox" checked={statusFilter.has('ACTIVE')} onChange={() => toggle(statusFilter, setStatusFilter, 'ACTIVE')} />
              {t('فعّال', 'Active')}
            </label>
            <label className="dropdown__item">
              <input
                type="checkbox"
                checked={statusFilter.has('INACTIVE')}
                onChange={() => toggle(statusFilter, setStatusFilter, 'INACTIVE')}
              />
              {t('موقوف', 'Inactive')}
            </label>
          </div>
        </details>
        {siteOptions.length ? (
          <details className="dropdown">
            <summary className="btn">
              {t('المشروع', 'Project')}
              {siteFilter.size ? ` (${siteFilter.size})` : ''}
            </summary>
            <div className="dropdown__panel">
              {siteOptions.map((name) => (
                <label key={name} className="dropdown__item">
                  <input type="checkbox" checked={siteFilter.has(name)} onChange={() => toggle(siteFilter, setSiteFilter, name)} />
                  {name}
                </label>
              ))}
            </div>
          </details>
        ) : null}
      </div>

      <Card>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !visible.length ? (
          <EmptyState title={t('لا توجد نتائج مطابقة', 'No matching results')} icon="search" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('الاسم', 'Name')}</th>
                  <th scope="col">{t('البريد', 'Email')}</th>
                  <th scope="col">{t('الدور', 'Role')}</th>
                  <th scope="col">{t('المشاريع', 'Projects')}</th>
                  <th scope="col">{t('آخر دخول', 'Last sign-in')}</th>
                  <th scope="col">{t('الحالة', 'Status')}</th>
                  <th scope="col">
                    <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((user) => {
                  const roles = rolesOf(user)
                  return (
                    <tr key={user.id} className={user.isActive ? undefined : 'is-inactive'}>
                      <td>
                        <strong>{user.name}</strong>
                        {user.id === me?.id ? <span className="muted small"> {t('(أنت)', '(you)')}</span> : null}
                      </td>
                      <td dir="ltr" className="ltr-cell">
                        {user.email}
                      </td>
                      <td>{roles.length ? roles.map((role) => ROLE_LABELS[role]).join(listSeparator) : <span className="muted">{t('بدون دور', 'No role')}</span>}</td>
                      <td>
                        {user.memberships.length ? (
                          user.isAdmin ? (
                            t('كل المشاريع', 'All projects')
                          ) : (
                            user.memberships.map((membership) => membership.projectName).join(listSeparator)
                          )
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : <span className="muted">{t('لم يسجّل الدخول بعد', 'Has not signed in yet')}</span>}</td>
                      <td>{user.isActive ? t('فعّال', 'Active') : t('موقوف', 'Inactive')}</td>
                      <td>
                        <div className="row-actions">
                          <Button size="sm" variant="ghost" onClick={() => setResetting(user)}>
                            {t('كلمة المرور', 'Password')}
                          </Button>
                          {user.id !== me?.id ? (
                            <Button
                              size="sm"
                              variant={user.isActive ? 'ghost' : 'secondary'}
                              busy={busy === `act${user.id}`}
                              onClick={async () => {
                                if (
                                  user.isActive &&
                                  !(await confirm(t(`إيقاف حساب ${user.name}؟ لن يتمكن من تسجيل الدخول بعد ذلك.`, `Deactivate ${user.name}? They will not be able to sign in.`), {
                                    confirmLabel: t('إيقاف', 'Deactivate'),
                                  }))
                                )
                                  return
                                const next = await run(`act${user.id}`, () => api.updateUser(user.id, { isActive: !user.isActive }))
                                if (next) replace(next)
                              }}
                            >
                              {user.isActive ? t('إيقاف', 'Deactivate') : t('تفعيل', 'Activate')}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating ? (
        <UserFormModal
          allowAdmin
          onClose={() => setCreating(false)}
          onCreated={(user) => {
            setData((current) => [...(current ?? []), { ...user, memberships: [] }])
            setCreating(false)
          }}
        />
      ) : null}

      {inviting ? <InviteSheet projects={projects ?? []} busy={busy === 'invite'} onClose={() => setInviting(false)} onSent={sendInvite} /> : null}

      {resetting ? (
        <Modal title={t(`تغيير كلمة مرور ${resetting.name}`, `Change password for ${resetting.name}`)} onClose={() => setResetting(null)}>
          <form
            className="stack"
            onSubmit={async (event) => {
              event.preventDefault()
              const next = await run('pw', () => api.updateUser(resetting.id, { password }), t('تم تغيير كلمة المرور', 'Password changed'))
              if (next) {
                setResetting(null)
                setPassword('')
              }
            }}
          >
            <TextField
              label={t('كلمة المرور الجديدة', 'New password')}
              type="password"
              dir="ltr"
              autoComplete="new-password"
              hint={t('8 أحرف على الأقل', 'At least 8 characters')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <div className="form-actions">
              <Button onClick={() => setResetting(null)}>{t('إلغاء', 'Cancel')}</Button>
              <Button type="submit" variant="primary" busy={busy === 'pw'} disabled={password.length < 8}>
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  )
}
